// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import L from 'leaflet';
import type { ViewContext } from '../viewContext';
import { loadGeo, stateColour } from '../data';
import { RAMP, rampColour } from '../components/charts';
import type { Postcode } from '../types';
import { metricValue, type Metric } from '../analysis';
import { glossaryTerm } from '../glossary';
import { escapeHtml, formatNumber, formatPercent } from '../utils/format';

const METRICS: Record<Metric, { label: string; title: string; sub: string; stops: number[]; fmt: (p: Postcode) => string; unit: string }> = {
  bev: {
    label: 'Electric %',
    title: 'Battery-electric share of the car fleet',
    sub: 'Share of passenger vehicles that are fully electric. A stark divide: affluent inner suburbs and the ACT against near-zero in the regions.',
    stops: [0.4, 0.8, 1.4, 2.2, 3.5, 6],
    fmt: (p) => formatPercent(metricValue(p, 'bev')),
    unit: '% electric',
  },
  ev: {
    label: 'Electrified %',
    title: 'Electrified share (electric + hybrid)',
    sub: 'Share of passenger vehicles that are electric or hybrid. Hybrids spread the pattern a little wider than pure electric.',
    stops: [2, 3.5, 5, 7, 10, 15],
    fmt: (p) => formatPercent(metricValue(p, 'ev')),
    unit: '% electrified',
  },
  diesel: {
    label: 'Diesel %',
    title: 'Diesel share of the car fleet',
    sub: 'Share of passenger vehicles running on diesel. Highest in rural and remote postcodes where long distances favour diesel.',
    stops: [10, 14, 18, 23, 29, 38],
    fmt: (p) => formatPercent(metricValue(p, 'diesel')),
    unit: '% diesel',
  },
  petrol: {
    label: 'Petrol %',
    title: 'Petrol share of the car fleet',
    sub: 'Share of passenger vehicles running on petrol — still the overwhelming majority almost everywhere.',
    stops: [68, 73, 77, 81, 84, 87],
    fmt: (p) => formatPercent(metricValue(p, 'petrol')),
    unit: '% petrol',
  },
  total: {
    label: 'Total cars',
    title: 'Total passenger vehicles',
    sub: 'Raw counts — largely a map of where people live. Switch to a share metric to see the fuel pattern.',
    stops: [2000, 6000, 12000, 20000, 30000, 45000],
    fmt: (p) => formatNumber(metricValue(p, 'total')),
    unit: 'cars',
  },
};

const MAINLAND = L.latLngBounds([-43.8, 112.5], [-9.8, 154.2]);

export function renderMap(root: HTMLElement, ctx: ViewContext): void {
  let metric: Metric = (localStorage.getItem('veh.map.metric') as Metric) || 'bev';
  if (!METRICS[metric]) metric = 'bev';

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">Australia's electric-car map</h1>
      <p class="view-sub">
        Every ${glossaryTerm('poa', 'postal area')}, shaded by the fuel mix of its passenger cars. Electric uptake is
        concentrated in a few wealthy postcodes and the ACT. Hover any postcode; click to open it.
      </p>
    </div>
    <div class="controls">
      <span class="field-label">Colour by</span>
      <div class="seg" role="group" data-role="metric">
        ${(Object.keys(METRICS) as Metric[]).map((m) => `<button class="seg-btn" data-metric="${m}" aria-pressed="${m === metric}">${METRICS[m].label}</button>`).join('')}
      </div>
    </div>
    <div class="panel">
      <div class="panel-title" data-role="title"></div>
      <div class="panel-sub" data-role="sub"></div>
      <div class="map-frame"><div class="map-canvas"></div></div>
      <div class="map-legend" data-role="legend"></div>
    </div>
  `;

  const canvas = root.querySelector('.map-canvas') as HTMLElement;
  const titleEl = root.querySelector('[data-role="title"]') as HTMLElement;
  const subEl = root.querySelector('[data-role="sub"]') as HTMLElement;
  const legendEl = root.querySelector('[data-role="legend"]') as HTMLElement;

  const map = L.map(canvas, { minZoom: 3, maxZoom: 12, scrollWheelZoom: false, preferCanvas: true });
  map.attributionControl.setPrefix(false);
  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
    attribution: 'Tiles © CARTO',
    subdomains: 'abcd',
    minZoom: 3,
    maxZoom: 12,
  }).addTo(map);
  map.fitBounds(MAINLAND, { padding: [6, 6] });

  const valueOf = (pc: string): number | null => {
    const p = ctx.data.byPostcode.get(pc);
    if (!p) return null;
    return metricValue(p, metric);
  };
  let layer: L.GeoJSON | null = null;
  const styleFor = (pc: string) => ({
    fillColor: rampColour(valueOf(pc), METRICS[metric].stops),
    fillOpacity: 0.82,
    color: '#ffffff',
    weight: 0.35,
  });

  const renderLegend = () => {
    const cfg = METRICS[metric];
    const labels = [0, ...cfg.stops];
    legendEl.innerHTML = `
      <span class="field-label">${escapeHtml(cfg.unit)}</span>
      <span class="scale-bar">${RAMP.map((c) => `<span class="scale-swatch" style="background:${c}"></span>`).join('')}</span>
      <span style="font-family:var(--font-mono);font-size:var(--font-size-xs)">${labels.map((v) => (metric === 'total' ? (v >= 1000 ? `${v / 1000}k` : v) : v)).join(' · ')}+</span>
      <span class="legend-item" style="cursor:default;margin-left:auto"><span class="legend-swatch" style="background:#e2e8f0"></span>No / few cars</span>
    `;
  };

  const paint = () => {
    const cfg = METRICS[metric];
    titleEl.textContent = cfg.title;
    subEl.textContent = cfg.sub;
    renderLegend();
    if (layer) layer.setStyle((f: any) => styleFor(f.properties.pc));
    root.querySelectorAll('[data-metric]').forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute('data-metric') === metric)));
  };

  root.querySelector('[data-role="metric"]')?.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-metric]');
    if (!btn) return;
    metric = btn.getAttribute('data-metric') as Metric;
    localStorage.setItem('veh.map.metric', metric);
    paint();
  });

  loadGeo()
    .then((geo) => {
      layer = L.geoJSON(geo, {
        attribution: 'Boundaries: ABS ASGS 2021 (CC BY 4.0) · Data: BITRE Road Vehicles Australia',
        style: (f: any) => styleFor(f.properties.pc),
        onEachFeature: (f: any, lyr: L.Layer) => {
          const pc = f.properties.pc as string;
          const p = ctx.data.byPostcode.get(pc);
          const name = p?.loc ? `${pc} · ${p.loc}` : pc;
          const tip = p
            ? `<strong>${escapeHtml(name)}</strong><br>` +
              `${formatPercent(metricValue(p, 'bev'))} electric · ${formatPercent(metricValue(p, 'diesel'))} diesel<br>` +
              `<span style="color:#475569">${formatNumber(p.total)} cars · ${p.st}</span>`
            : `<strong>${escapeHtml(pc)}</strong><br><span style="color:#475569">No data</span>`;
          lyr.bindTooltip(tip, { sticky: true, className: 'map-tip' });
          lyr.on({
            mouseover: () => (lyr as L.Path).setStyle({ weight: 2, color: '#0f172a' }),
            mouseout: () => layer?.resetStyle(lyr as L.Path),
            click: () => ctx.openPostcode(pc),
          });
        },
      }).addTo(map);
      requestAnimationFrame(() => {
        map.invalidateSize();
        map.fitBounds(MAINLAND, { padding: [6, 6] });
      });
      paint();
    })
    .catch(() => {
      canvas.innerHTML = '<div class="error-box" style="margin:var(--space-lg)">Could not load the postcode boundaries.</div>';
    });

  paint();

  // State quick-zoom.
  const zooms: Record<string, [number, number, number]> = {
    NSW: [-32.5, 147, 6],
    VIC: [-37, 145, 6],
    QLD: [-22, 145, 5],
    SA: [-32, 135, 5],
    WA: [-26, 121, 5],
    TAS: [-42, 146.5, 7],
    NT: [-19, 133, 5],
    ACT: [-35.3, 149.1, 9],
  };
  const zoomBar = document.createElement('div');
  zoomBar.className = 'controls';
  zoomBar.style.marginTop = 'var(--space-md)';
  zoomBar.innerHTML =
    '<span class="field-label">Jump to</span>' +
    Object.keys(zooms).map((s) => `<button class="chip" data-zoom="${s}" style="color:${stateColour(s)};font-weight:700">${s}</button>`).join('') +
    '<button class="chip" data-zoom="ALL">All of Australia</button>';
  (root.querySelector('.panel') as HTMLElement).appendChild(zoomBar);
  zoomBar.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-zoom]');
    if (!btn) return;
    const s = btn.getAttribute('data-zoom') as string;
    if (s === 'ALL') {
      map.fitBounds(MAINLAND, { padding: [6, 6] });
      return;
    }
    const [lat, lng, z] = zooms[s];
    map.setView([lat, lng], z);
  });
}
